/* eslint-disable react-hooks/exhaustive-deps */
import React, {useState, useEffect, useRef} from "react"
import {EditorState, ContentState, convertToRaw} from "draft-js"
import {Editor} from "react-draft-wysiwyg"
import draftToHtml from "draftjs-to-html"
import htmlToDraft from "html-to-draftjs"
import {useTranslation} from "react-i18next"
import styles from "./BaseEditor.module.css"
import cx from "classnames"

const createEditorStateFromValue = (val: string | undefined | null) => {
    if (!val) {
        return EditorState.createEmpty()
    }

    try {
        const contentBlock = htmlToDraft(val)
        if (contentBlock && contentBlock.contentBlocks && contentBlock.contentBlocks.length > 0) {
            const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks, contentBlock.entityMap)
            return EditorState.createWithContent(contentState)
        }
        return EditorState.createEmpty()
    } catch {
        try {
            const contentBlock = htmlToDraft(`<p>${val}</p>`)
            if (contentBlock && contentBlock.contentBlocks && contentBlock.contentBlocks.length > 0) {
                const contentState = ContentState.createFromBlockArray(
                    contentBlock.contentBlocks,
                    contentBlock.entityMap
                )
                return EditorState.createWithContent(contentState)
            }
        } catch {
            console.warn("Failed to parse editor content, using empty state")
        }
        return EditorState.createEmpty()
    }
}

export function BaseEditor(props) {
    const {value, onChange, defaultValue = "", error = false} = props
    const {t} = useTranslation(["common"])
    const initialValue = value !== undefined && value !== null ? value : defaultValue
    const [editorState, setEditorState] = useState(() => createEditorStateFromValue(initialValue))
    const lastOutputValueRef = useRef<string | null>(initialValue || null)
    const isInternalUpdateRef = useRef(false)

    useEffect(() => {
        if (isInternalUpdateRef.current) {
            isInternalUpdateRef.current = false
            return
        }

        if (value !== undefined && value !== null) {
            if (value !== lastOutputValueRef.current) {
                setEditorState(createEditorStateFromValue(value))
                lastOutputValueRef.current = value
            }
        } else if (value === undefined || value === null) {
            if (lastOutputValueRef.current !== null) {
                setEditorState(EditorState.createEmpty())
                lastOutputValueRef.current = null
            }
        }
    }, [value])

    const onEditorStateChange = (editorState) => {
        setEditorState(editorState)
        const newValue = editorState.getCurrentContent().getPlainText().length
            ? draftToHtml(convertToRaw(editorState.getCurrentContent()))
            : ""

        isInternalUpdateRef.current = true
        lastOutputValueRef.current = newValue

        if (onChange) {
            onChange(newValue)
        }
    }

    return (
        <Editor
            placeholder={t("common:message.beginTyping")}
            editorState={editorState}
            toolbarClassName={styles.toolbar}
            wrapperClassName={cx(styles.wrap, {[styles.error]: error})}
            editorClassName={styles.editorWrap}
            onEditorStateChange={onEditorStateChange}
            toolbar={{
                options: ["inline", "blockType", "fontSize", "emoji", "list", "textAlign", "link", "history"],
                inline: {
                    inDropdown: false,
                    options: ["bold", "italic", "underline", "strikethrough"]
                },
                blockType: {
                    inDropdown: true,
                    options: ["Normal", "H1", "H2", "H3", "H4", "H5", "H6", "Blockquote", "Code"],
                    className: undefined,
                    component: undefined,
                    dropdownClassName: undefined
                },
                fontSize: {
                    // icon: fontSize,
                    options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
                    className: undefined,
                    component: undefined,
                    dropdownClassName: undefined
                },
                list: {inDropdown: true},
                textAlign: {inDropdown: true},
                link: {inDropdown: false}
            }}
        />
    )
}
